import type { AssistantStep, JobActivity } from "./types";

export type ActivityTool = Omit<AssistantStep["tools"][number], "status"> & {
  id?: string;
  status: JobActivity["steps"][number]["tools"][number]["status"];
};

export type ActivityStep = {
  id?: string;
  text: string | null;
  tools: ActivityTool[];
};

export type ActivityState = "running" | "paused" | "interrupted" | "finished";

/** Tool-only steps belong under the preceding commentary, not in dozens of separate blocks. */
export function groupActivitySteps(steps: ActivityStep[]): ActivityStep[] {
  const groups: ActivityStep[] = [];
  for (const [index, step] of steps.entries()) {
    const previous = groups.at(-1);
    if (!step.text && previous) {
      previous.tools.push(...step.tools);
    } else {
      groups.push({ ...step, id: step.id ?? String(index), tools: [...step.tools] });
    }
  }
  return groups;
}

export function getToolDisplayStatus(tool: ActivityTool, state: ActivityState) {
  if (tool.status === "completed" || tool.status === "error") return tool.status;
  if (state === "interrupted" || state === "finished") return "interrupted";
  if (state === "paused") return "paused";
  return tool.status;
}

/** Keep active tools visible even when newer completed calls appear in parallel. */
export function getActivityPreview(tools: ActivityTool[], showRecent: boolean): ActivityTool[] {
  if (!showRecent) return [];
  const active = tools.filter((tool) => tool.status === "running" || tool.status === "pending");
  const recent = tools
    .filter((tool) => !active.includes(tool))
    .slice(-Math.max(0, 3 - active.length));
  const selected = new Set([...active.slice(-3), ...(active.length >= 3 ? [] : recent)]);
  return tools.filter((tool) => selected.has(tool));
}
